import { Save } from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useState } from 'react';

import CAccountDeleteForm from '../../components/CAccountDeleteForm';
import CButton from '../../components/CButton';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CustomTextField from '../../components/CustomTextField';
import PageModuleFormGroup from '../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../components/PageModuleFormRow';
import Page from '../../components/layout/Page';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppDispatch } from '../../hooks/hooks';
import type { IRequestState } from '../../models/IRequestState';
import UserManagementService from '../../services/userManagementService';
import { updateSnackbar } from '../../store/appSlice';

// Keycloak refresh threshold.
// Our access token normally expires after about 300 seconds.
// We use the same value so updateToken refreshes it immediately,
// which ensures new profile data like name and email appears everywhere right away.
const KEYCLOAK_MIN_TOKEN_VALIDITY = 300;

interface Form {
  firstName: string;
  lastName: string;
  email: string;
}

interface State {
  reqState: IRequestState;
  form: Partial<Form>;
}

export default function AccountSelfManagement() {
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();

  const initialForm = {
    firstName: keycloak.tokenParsed?.given_name?.trim() ?? '',
    lastName: keycloak.tokenParsed?.family_name?.trim() ?? '',
    email: keycloak.tokenParsed?.email?.trim() ?? ''
  };

  const [state, setState] = useState<State>({
    reqState: { isLoading: false, isError: false },
    form: {
      firstName: keycloak.tokenParsed?.given_name,
      lastName: keycloak.tokenParsed?.family_name,
      email: keycloak.tokenParsed?.email
    }
  });

  const breadCrumbText = CLocalizedTextStringStatic('selfServiceAccount.page.title');
  const firstNameLabel = CLocalizedTextStringStatic('textField.label.firstName');
  const lastNameLabel = CLocalizedTextStringStatic('textField.label.lastName');
  const emailLabel = CLocalizedTextStringStatic('textField.label.email');
  const passwordHeading = CLocalizedTextStringStatic('resetPassword.heading.text');

  const handleDelete = async () => {
    const apiResponse = await UserManagementService.deleteSelf();
    if (apiResponse.wasSuccessful()) {
      const result = apiResponse.getData() as { message: string };
      dispatch(updateSnackbar({ message: result.message, severity: 'success' }));
      await keycloak.logout({
        redirectUri: window.location.origin + '/news'
      });
    } else {
      const errorMessage = apiResponse.getErrorString();
      dispatch(updateSnackbar({ message: errorMessage, severity: 'error' }));
    }
  };

  const handlePasswordReset = async () => {
    const apiResponse = await UserManagementService.resetPasswordSelf();
    if (apiResponse.wasSuccessful()) {
      const result = apiResponse.getData() as { data: string; success: boolean; message: string };
      dispatch(updateSnackbar({ message: result.message, severity: 'success' }));
    } else {
      const errorMessage = apiResponse.getErrorString();
      dispatch(updateSnackbar({ message: errorMessage, severity: 'error' }));
    }
  };

  const sanitizedForm = {
    firstName: state.form.firstName?.trim() ?? '',
    lastName: state.form.lastName?.trim() ?? '',
    email: state.form.email?.trim() ?? ''
  };

  const isFormChanged =
    sanitizedForm.firstName !== initialForm.firstName ||
    sanitizedForm.lastName !== initialForm.lastName ||
    sanitizedForm.email !== initialForm.email;

  const isUpdateDisabled =
    !sanitizedForm.firstName || !sanitizedForm.lastName || !sanitizedForm.email || !isFormChanged;

  const updateHandler = async () => {
    if (isUpdateDisabled) return true;

    updateStateAttribute<State>({ reqState: { ...state.reqState, isLoading: true, isError: false } }, setState);

    const res = await UserManagementService.updateSelfAccount(sanitizedForm);

    updateStateAttribute<State>(
      {
        reqState: {
          isLoading: false,
          isError: res.hasError(),
          strError: res.getErrorString()
        }
      },
      setState
    );

    if (res.wasSuccessful()) {
      await keycloak.updateToken(KEYCLOAK_MIN_TOKEN_VALIDITY);
      const result = res.getData() as { success: boolean; message: string };
      dispatch(updateSnackbar({ message: result.message, severity: 'success' }));
    } else {
      dispatch(updateSnackbar({ message: res.getErrorString(), severity: 'error' }));
    }

    return true;
  };

  return (
    <Page title={breadCrumbText}>
      <>
        <CPageModule>
          <h3>
            <CLocalizedText dictKey={'selfServiceAccount.page.module1'} />
          </h3>

          <PageModuleFormGroup>
            <PageModuleFormRow>
              <CustomTextField
                label={firstNameLabel}
                value={state.form.firstName ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, firstName: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>

            <PageModuleFormRow>
              <CustomTextField
                label={lastNameLabel}
                value={state.form.lastName ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, lastName: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>

            <PageModuleFormRow>
              <CustomTextField
                label={emailLabel}
                value={state.form.email ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, email: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>
          </PageModuleFormGroup>

          <PageModuleFormRow>
            <CButton
              style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
              icons={{ start: <Save /> }}
              onClick={updateHandler}
              disabled={isUpdateDisabled}
              state={state.reqState}
            >
              <CLocalizedText dictKey={'update.btn.text'} />
            </CButton>
          </PageModuleFormRow>
        </CPageModule>

        <CPageModule>
          <h3>{passwordHeading} </h3>
          <PageModuleFormRow
            description={
              <p>
                <CLocalizedText dictKey={'resetPassword.btn.description.text'} />
              </p>
            }
          >
            <CButton
              style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
              onClick={handlePasswordReset}
              isForm
            >
              <CLocalizedText dictKey={'resetPassword.btn.text'} />
            </CButton>
          </PageModuleFormRow>
        </CPageModule>

        {keycloak.tokenParsed?.email && (
          <CPageModule>
            <h3>
              <CLocalizedText dictKey={'accountDelete.heading'} />
            </h3>
            <CAccountDeleteForm
              selfDelete={true}
              emailAddress={keycloak.tokenParsed?.email ?? ''}
              onCancel={() => {}}
              onDelete={handleDelete}
            />
          </CPageModule>
        )}
      </>
    </Page>
  );
}
